import React, { useState, useEffect } from "react";
import Avatar from "@mui/joy/Avatar";
import Box from "@mui/joy/Box";
import Chip from "@mui/joy/Chip";
import Table from "@mui/joy/Table";
import Sheet from "@mui/joy/Sheet";
import IconButton from "@mui/joy/IconButton";
import Typography from "@mui/joy/Typography";
import CircularProgress from "@mui/joy/CircularProgress";
import Input from "@mui/joy/Input";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { ColorPaletteProp } from "@mui/joy/styles";
import Menu from "@mui/joy/Menu";
import MenuItem from "@mui/joy/MenuItem";
import Snackbar from "@mui/joy/Snackbar";
import Dropdown from "@mui/joy/Dropdown";
import MenuButton from "@mui/joy/MenuButton";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import Select from "@mui/joy/Select";
import Option from "@mui/joy/Option";
import FormControl from "@mui/joy/FormControl";
import Drawer from "@mui/joy/Drawer";
import ModalClose from "@mui/joy/ModalClose";
import Button from "@mui/joy/Button";
import Checkbox from "@mui/joy/Checkbox";
import Divider from "@mui/joy/Divider";
import { gql, useQuery, useMutation } from "@apollo/client";
import Pagination from "../components/Pagination";
import { usePagination } from "../components/usePagination";
import { getUserProfile } from "../jwt";
import {
  useActionCable,
  useChannel,
} from "@aersoftware/react-use-action-cable";
import { getWebSocketUrl } from "../utils/actionCableUrl";

// Import role images
import assetOwnerImg from "../../assets/images/asset_owner.png";
import backOfficeOfficialImg from "../../assets/images/back_office_official.png";
import fieldOfficialImg from "../../assets/images/field_official.png";
import integratorImg from "../../assets/images/integrator.png";
import kioskImg from "../../assets/images/kiosk.png";
import partnerImg from "../../assets/images/partner.png";
import serviceProviderImg from "../../assets/images/service_provider.png";
import superUserImg from "../../assets/images/super_user.png";
import systemAdministratorImg from "../../assets/images/system_administrator.png";
import systemServiceSupportImg from "../../assets/images/system_service_support.png";
import systemSupportImg from "../../assets/images/system_support.png";

const USERS_QUERY = gql`
  query Users(
    $first: Int
    $after: String
    $last: Int
    $before: String
    $filters: UserFilter
  ) {
    users(
      first: $first
      after: $after
      last: $last
      before: $before
      filters: $filters
    ) {
      edges {
        node {
          createdAt
          email
          firstName
          id
          lastLoggedInAt
          lastName
          roles
          status
        }
        cursor
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
    }
  }
`;

const ACTIVATE_USER_MUTATION = gql`
  mutation ActivateUser($input: ActivateUserInput!) {
    activateUser(input: $input) {
      user {
        id
        status
      }
    }
  }
`;

const DEACTIVATE_USER_MUTATION = gql`
  mutation DeactivateUser($input: DeactivateUserInput!) {
    deactivateUser(input: $input) {
      user {
        id
        status
      }
    }
  }
`;

const UPDATE_USER_ROLE_MUTATION = gql`
  mutation UpdateUserRole($input: UpdateUserRoleInput!) {
    updateUserRole(input: $input) {
      user {
        id
        roles
      }
    }
  }
`;

const RowMenu = ({
  row,
  onEditRoles,
  refetch,
}: {
  row: any;
  onEditRoles: (row: any) => void;
  refetch: () => void;
}) => {
  const [activateUser] = useMutation(ACTIVATE_USER_MUTATION, {
    onCompleted: () => {
      refetch();
    },
    update: (cache, { data }) => {
      if (data?.activateUser?.user) {
        cache.modify({
          fields: {
            users(existingUsersRef, { readField }) {
              // Force Apollo to re-evaluate the query with current filters
              return existingUsersRef;
            },
          },
        });
        cache.evict({ fieldName: "users" });
        cache.gc();
      }
    },
  });
  const [deactivateUser] = useMutation(DEACTIVATE_USER_MUTATION, {
    onCompleted: () => {
      refetch();
    },
    update: (cache, { data }) => {
      if (data?.deactivateUser?.user) {
        cache.modify({
          fields: {
            users(existingUsersRef, { readField }) {
              // Force Apollo to re-evaluate the query with current filters
              return existingUsersRef;
            },
          },
        });
        cache.evict({ fieldName: "users" });
        cache.gc();
      }
    },
  });
  const [open, setOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleError = (error) => {
    setErrorMessage(error?.message || "An error occurred");
    setOpen(true);
  };

  // Check if current user is SUPER_USER
  const currentUserProfile = getUserProfile();
  const isSuperUser = currentUserProfile?.currentRole === "SUPER_USER";

  return (
    <>
      <Dropdown>
        <MenuButton
          slots={{ root: IconButton }}
          slotProps={{
            root: { variant: "plain", color: "neutral", size: "sm" },
          }}
        >
          <MoreHorizRoundedIcon />
        </MenuButton>
        <Menu size="sm" sx={{ minWidth: 140 }}>
          {row.status === "INACTIVE" ? (
            <MenuItem
              onClick={() =>
                activateUser({
                  variables: { input: { id: row.id } },
                  onError: handleError,
                })
              }
            >
              Activate
            </MenuItem>
          ) : (
            isSuperUser && (
              <MenuItem
                onClick={() =>
                  deactivateUser({
                    variables: { input: { id: row.id } },
                    onError: handleError,
                  })
                }
              >
                Deactivate
              </MenuItem>
            )
          )}
          {isSuperUser && (
            <MenuItem onClick={() => onEditRoles(row)}>
              Edit User Roles
            </MenuItem>
          )}
        </Menu>
      </Dropdown>
      <Snackbar
        open={open}
        color="danger"
        variant="soft"
        onClose={() => setOpen(false)}
        autoHideDuration={3000}
      >
        {errorMessage}
      </Snackbar>
    </>
  );
};

const Row = (props: {
  row: any;
  key?: any;
  onEditRoles: (row: any) => void;
  refetch: () => void;
}) => {
  const { row, onEditRoles, refetch } = props;

  const getRoleImage = (role: string) => {
    // Map role to imported image
    const roleImageMap: { [key: string]: string } = {
      ASSET_OWNER: assetOwnerImg,
      BACK_OFFICE_OFFICIAL: backOfficeOfficialImg,
      FIELD_OFFICIAL: fieldOfficialImg,
      INTEGRATOR: integratorImg,
      KIOSK: kioskImg,
      PARTNER: partnerImg,
      SERVICE_PROVIDER: serviceProviderImg,
      SUPER_USER: superUserImg,
      SYSTEM_ADMINISTRATOR: systemAdministratorImg,
      SYSTEM_SERVICE_SUPPORT: systemServiceSupportImg,
      SYSTEM_SUPPORT: systemSupportImg,
    };
    return roleImageMap[role] || "";
  };

  const formatRole = (role: string) => {
    return role
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  return (
    <React.Fragment>
      <tr>
        <td>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <Avatar size="sm" alt={`${row.firstName} ${row.lastName}`} />
            <div>
              <Typography level="body-xs">
                {row.firstName} {row.lastName}
              </Typography>
              <Typography
                level="body-xs"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "200px",
                }}
              >
                {row.email}
              </Typography>
            </div>
          </Box>
        </td>
        <td>
          <Box
            sx={{
              display: "flex",
              gap: 0.5,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            {row.roles.map((role: string) => (
              <img
                key={role}
                src={getRoleImage(role)}
                alt={formatRole(role)}
                title={formatRole(role)}
                style={{
                  height: "24px",
                  width: "auto",
                  objectFit: "contain",
                }}
                onError={(e) => {
                  // Fallback to chip if image fails to load
                  console.error(`Failed to load image for role: ${role}`);
                }}
              />
            ))}
          </Box>
        </td>
        <td>
          <Typography level="body-xs">
            {new Intl.DateTimeFormat("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            }).format(new Date(row.createdAt))}
          </Typography>
        </td>
        <td>
          <Chip
            variant="soft"
            size="sm"
            color={
              {
                ACTIVE: "success",
                INACTIVE: "warning",
              }[row.status] as ColorPaletteProp
            }
          >
            {row.status}
          </Chip>
        </td>
        <td>
          {/* Show menu only for SUPER_USER */}
          {(() => {
            const currentUserProfile = getUserProfile();
            const isSuperUser =
              currentUserProfile?.currentRole === "SUPER_USER";

            return isSuperUser ? (
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <RowMenu
                  row={row}
                  onEditRoles={onEditRoles}
                  refetch={refetch}
                />
              </Box>
            ) : null;
          })()}
        </td>
      </tr>
    </React.Fragment>
  );
};

const formatDateForAPI = (date: string | undefined) => {
  if (!date) return undefined;
  return new Date(date).toISOString();
};

const UsersTable = ({
  name,
  selectedRoles,
  selectedStatus,
  registrationDateFrom,
  registrationDateTo,
  onEditRoles,
}: {
  name: string;
  selectedRoles: string[];
  selectedStatus: string | null;
  registrationDateFrom: string | undefined;
  registrationDateTo: string | undefined;
  onEditRoles: (row: any) => void;
}) => {
  const [pageSize] = useState(10);

  const { data, loading, error, fetchMore, refetch } = useQuery(USERS_QUERY, {
    variables: {
      first: pageSize,
      filters: {
        name: name || undefined,
        roles: selectedRoles.length > 0 ? selectedRoles : undefined,
        status: selectedStatus || undefined,
        registrationDateRange: {
          start: formatDateForAPI(registrationDateFrom),
          end: formatDateForAPI(registrationDateTo),
        },
      },
    },
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const {
    pageInfo,
    isLoadingMore,
    handlePageChange,
    canGoNext,
    canGoPrevious,
  } = usePagination({
    pageSize,
    data,
    fetchMore,
    getPageInfo: (data) => data.users.pageInfo,
    updateQuery: (prev, { fetchMoreResult }) => {
      if (!fetchMoreResult) return prev;
      return {
        users: {
          ...fetchMoreResult.users,
          edges: [...fetchMoreResult.users.edges],
        },
      };
    },
  });

  // Setup WebSocket connection
  const { actionCable } = useActionCable(getWebSocketUrl());
  const channel = useChannel(actionCable);

  useEffect(() => {
    channel.subscribe(
      { channel: "ApplicationChannel" },
      {
        received: (wsData) => {
          if (
            wsData.type === "user_updated" ||
            wsData.type === "profile_updated"
          ) {
            refetch();
          }
        },
      }
    );

    return () => {
      channel.unsubscribe();
    };
  }, [refetch]);

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <Typography level="body-lg" color="danger">
          Error loading data: {error.message}
        </Typography>
      </Box>
    );
  }

  return (
    <React.Fragment>
      <Sheet
        className="OrderTableContainer"
        variant="outlined"
        sx={{
          display: { xs: "none", sm: "initial" },
          width: "100%",
          borderRadius: "sm",
          flexShrink: 1,
        }}
      >
        <Table
          aria-labelledby="tableTitle"
          hoverRow
          sx={{
            "--TableCell-headBackground":
              "var(--joy-palette-background-level1)",
            "--Table-headerUnderlineThickness": "1px",
            "--TableRow-hoverBackground":
              "var(--joy-palette-background-level1)",
            "--TableCell-paddingY": "4px",
            "--TableCell-paddingX": "8px",
          }}
        >
          <thead>
            <tr>
              <th style={{ width: "20%", padding: "12px 6px" }}>Name</th>
              <th style={{ width: "44%", padding: "12px 6px" }}>Roles</th>
              <th style={{ width: "20%", padding: "12px 6px" }}>
                Registration Date
              </th>
              <th style={{ width: "10%", padding: "12px 6px" }}>Status</th>
              <th style={{ width: "3%", padding: "12px 6px" }}></th>
            </tr>
          </thead>
          <tbody>
            {loading || isLoadingMore ? (
              <tr>
                <td colSpan={5}>
                  <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                    <CircularProgress size="sm" />
                  </Box>
                </td>
              </tr>
            ) : data?.users?.edges?.length === 0 ? (
              <tr>
                <td colSpan={5}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      p: 4,
                    }}
                  >
                    <Typography level="body-lg" color="neutral">
                      {name ||
                      selectedRoles.length > 0 ||
                      selectedStatus ||
                      registrationDateFrom ||
                      registrationDateTo
                        ? "No users found matching your filters."
                        : "No users found."}
                    </Typography>
                  </Box>
                </td>
              </tr>
            ) : (
              data?.users?.edges?.map(({ node: row }) => (
                <Row
                  key={row.id}
                  row={row}
                  onEditRoles={onEditRoles}
                  refetch={refetch}
                />
              ))
            )}
          </tbody>
        </Table>
      </Sheet>
      <Pagination
        pageInfo={pageInfo}
        isLoadingMore={isLoadingMore}
        onPageChange={handlePageChange}
        canGoNext={canGoNext}
        canGoPrevious={canGoPrevious}
      />
    </React.Fragment>
  );
};

const Users = () => {
  const [name, setName] = useState("");
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [registrationDateFrom, setRegistrationDateFrom] = useState("");
  const [registrationDateTo, setRegistrationDateTo] = useState("");

  const [debouncedName, setDebouncedName] = useState("");

  // Edit roles drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [editedRoles, setEditedRoles] = useState<string[]>([]);

  // Snackbar state for success/error messages
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarColor, setSnackbarColor] = useState<"success" | "danger">(
    "success"
  );

  const [updateUserRole, { loading: isUpdatingRoles }] = useMutation(
    UPDATE_USER_ROLE_MUTATION
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedName(name);
    }, 300);

    return () => clearTimeout(timer);
  }, [name]);

  const roleOptions = [
    "ASSET_OWNER",
    "BACK_OFFICE_OFFICIAL",
    "FIELD_OFFICIAL",
    "INTEGRATOR",
    "PARTNER",
    "SERVICE_PROVIDER",
    "SYSTEM_ADMINISTRATOR",
    "SYSTEM_SERVICE_SUPPORT",
    "SYSTEM_SUPPORT",
    "KIOSK",
    "SUPER_USER",
  ];

  const statusOptions = ["ACTIVE", "INACTIVE"];

  const formatRole = (role: string) => {
    return role
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const handleEditRoles = (user: any) => {
    setSelectedUser(user);
    setEditedRoles(user.roles || []);
    setDrawerOpen(true);
  };

  // Check if current user is editing their own profile
  const currentUserProfile = getUserProfile();
  const isEditingSelf = selectedUser?.id === currentUserProfile?.id?.toString();

  // Check if roles have been changed
  const hasRolesChanged = () => {
    if (!selectedUser) return false;
    const originalRoles = [...(selectedUser.roles || [])].sort();
    const currentRoles = [...editedRoles].sort();
    return JSON.stringify(originalRoles) !== JSON.stringify(currentRoles);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedUser(null);
    setEditedRoles([]);
  };

  const handleToggleRole = (role: string) => {
    setEditedRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    );
  };

  const handleSaveRoles = async () => {
    if (!selectedUser || editedRoles.length === 0) {
      setSnackbarMessage("Please select at least one role");
      setSnackbarColor("danger");
      setSnackbarOpen(true);
      return;
    }

    try {
      const result = await updateUserRole({
        variables: {
          input: {
            userId: selectedUser.id,
            roles: editedRoles,
          },
        },
        refetchQueries: ["Users"],
        awaitRefetchQueries: true,
      });

      if (result.data?.updateUserRole?.message) {
        setSnackbarMessage(result.data.updateUserRole.message);
        setSnackbarColor("success");
        setSnackbarOpen(true);
      }

      handleCloseDrawer();
    } catch (error: any) {
      setSnackbarMessage(error.message || "Failed to update user roles");
      setSnackbarColor("danger");
      setSnackbarOpen(true);
    }
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          mb: 1,
          gap: 1,
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "start", sm: "center" },
          flexWrap: "wrap",
          justifyContent: "space-between",
        }}
      >
        <Typography level="h2" component="h1">
          Users
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          gap: 2,
          mb: 3,
          flexWrap: "wrap",
          alignItems: "flex-start",
          width: "100%",
        }}
      >
        <FormControl sx={{ flex: 1, minWidth: 220 }}>
          <Typography level="body-sm" sx={{ mb: 0.5 }}>
            Search for users
          </Typography>
          <Input
            placeholder="Search by name or email..."
            startDecorator={<SearchIcon />}
            endDecorator={
              name && (
                <IconButton
                  size="sm"
                  variant="plain"
                  color="neutral"
                  onClick={() => setName("")}
                >
                  <ClearIcon />
                </IconButton>
              )
            }
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </FormControl>

        <FormControl sx={{ flex: 1, minWidth: 160 }}>
          <Typography level="body-sm" sx={{ mb: 0.5 }}>
            Roles
          </Typography>
          <Select
            multiple
            value={selectedRoles}
            onChange={(event, newValue) =>
              setSelectedRoles(newValue as string[])
            }
            placeholder="All roles"
          >
            {roleOptions.map((role) => (
              <Option key={role} value={role}>
                {formatRole(role)}
              </Option>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ flex: 1, minWidth: 150 }}>
          <Typography level="body-sm" sx={{ mb: 0.5 }}>
            Start Date
          </Typography>
          <Input
            type="date"
            value={registrationDateFrom}
            onChange={(event) => setRegistrationDateFrom(event.target.value)}
          />
        </FormControl>

        <FormControl sx={{ flex: 1, minWidth: 150 }}>
          <Typography level="body-sm" sx={{ mb: 0.5 }}>
            End Date
          </Typography>
          <Input
            type="date"
            value={registrationDateTo}
            onChange={(event) => setRegistrationDateTo(event.target.value)}
          />
        </FormControl>

        <FormControl sx={{ flex: 1, minWidth: 120 }}>
          <Typography level="body-sm" sx={{ mb: 0.5 }}>
            Status
          </Typography>
          <Select
            value={selectedStatus}
            onChange={(event, newValue) =>
              setSelectedStatus(newValue as string)
            }
            placeholder="All status"
          >
            <Option value={null}>All status</Option>
            {statusOptions.map((status) => (
              <Option key={status} value={status}>
                {status}
              </Option>
            ))}
          </Select>
        </FormControl>
      </Box>

      <UsersTable
        name={debouncedName}
        selectedRoles={selectedRoles}
        selectedStatus={selectedStatus}
        registrationDateFrom={registrationDateFrom || undefined}
        registrationDateTo={registrationDateTo || undefined}
        onEditRoles={handleEditRoles}
      />

      {/* Edit User Roles Drawer */}
      <Drawer
        open={drawerOpen}
        onClose={handleCloseDrawer}
        anchor="right"
        size="md"
        slotProps={{
          content: {
            sx: {
              width: { xs: "100%", sm: 400 },
            },
          },
        }}
      >
        <ModalClose />
        <Box sx={{ p: 3 }}>
          <Typography level="h4" sx={{ mb: 3 }}>
            Edit User Roles
          </Typography>

          {selectedUser && (
            <>
              {/* User Info */}
              <Box sx={{ mb: 3 }}>
                <Box
                  sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2 }}
                >
                  <Avatar
                    size="lg"
                    alt={`${selectedUser.firstName} ${selectedUser.lastName}`}
                  />
                  <Box>
                    <Typography level="title-md">
                      {selectedUser.firstName} {selectedUser.lastName}
                    </Typography>
                    <Typography level="body-sm" color="neutral">
                      {selectedUser.email}
                    </Typography>
                  </Box>
                </Box>
                <Chip
                  variant="soft"
                  size="sm"
                  color={
                    {
                      ACTIVE: "success",
                      INACTIVE: "warning",
                    }[selectedUser.status] as ColorPaletteProp
                  }
                >
                  {selectedUser.status}
                </Chip>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Roles List */}
              <Typography level="title-sm" sx={{ mb: 2 }}>
                Select Roles
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {roleOptions.map((role) => {
                  const isChecked = editedRoles.includes(role);
                  const isLastRole = editedRoles.length === 1 && isChecked;
                  const isDisabled =
                    (isEditingSelf && role === "SUPER_USER") || isLastRole;

                  return (
                    <Checkbox
                      key={role}
                      label={formatRole(role)}
                      checked={isChecked}
                      onChange={() => handleToggleRole(role)}
                      disabled={isDisabled}
                    />
                  );
                })}
              </Box>

              {/* Action Buttons */}
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  mt: 4,
                  justifyContent: "flex-end",
                }}
              >
                <Button
                  variant="outlined"
                  color="neutral"
                  onClick={handleCloseDrawer}
                  disabled={isUpdatingRoles}
                >
                  Cancel
                </Button>
                <Button
                  variant="solid"
                  color="primary"
                  onClick={handleSaveRoles}
                  loading={isUpdatingRoles}
                  disabled={!hasRolesChanged() || isUpdatingRoles}
                >
                  Save
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Drawer>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbarOpen}
        color={snackbarColor}
        variant="soft"
        onClose={() => setSnackbarOpen(false)}
        autoHideDuration={3000}
      >
        {snackbarMessage}
      </Snackbar>
    </>
  );
};

export default Users;
